import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Divider, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconCircleCheck, IconClock } from '@tabler/icons-react';
import DealFinalSettlement from '@/components/app/deals/DealFinalSettlement';
import { usePaymentConfirm, type PaymentActionKind } from '@/hooks/useDeals';
import type { BargainStepsInfo } from '@/types/deals';

interface FinalPaymentStepProps {
  readonly dealId: number;
  readonly info: BargainStepsInfo;
}

interface PayAction {
  readonly kind: PaymentActionKind;
  readonly checkboxKey: string;
  readonly buttonKey: string;
}

function actionForState(ps: string, isBuyer: boolean, isSeller: boolean): PayAction | null {
  if (ps === 'WAIT_UNDERPAYMENT' && isBuyer) {
    return { kind: 'payUnderpayment', checkboxKey: 'app.deals.finalPayment.confirmPay', buttonKey: 'app.deals.finalPayment.btnPay' };
  }
  if (ps === 'WAIT_WIRE_UNDERPAYMENT_CONFIRMATION' && isSeller) {
    return { kind: 'confirmUnderpaymentReceived', checkboxKey: 'app.deals.finalPayment.confirmReceived', buttonKey: 'app.deals.finalPayment.btnReceived' };
  }
  if (ps === 'WAIT_OVERPAYMENT' && isSeller) {
    return { kind: 'returnOverpayment', checkboxKey: 'app.deals.finalPayment.confirmReturn', buttonKey: 'app.deals.finalPayment.btnReturn' };
  }
  if (ps === 'WAIT_RETURN_OVERPAYMENT_CONFIRMATION' && isBuyer) {
    return { kind: 'confirmOverpaymentReceived', checkboxKey: 'app.deals.finalPayment.confirmReceived', buttonKey: 'app.deals.finalPayment.btnReceived' };
  }
  return null;
}

function waitKey(ps: string, isBuyer: boolean, isSeller: boolean): string {
  switch (ps) {
    case 'WAIT_UPLOAD_UNDERPAYMENT':
      return isSeller ? 'app.deals.finalPayment.edoSignInvoice' : 'app.deals.finalPayment.waitInvoice';
    case 'WAIT_UNDERPAYMENT':
      return 'app.deals.finalPayment.waitBuyerPay';
    case 'WAIT_WIRE_UNDERPAYMENT_CONFIRMATION':
      return 'app.deals.finalPayment.waitSellerConfirm';
    case 'WAIT_UPLOAD_OVERPAYMENT':
      return isBuyer ? 'app.deals.finalPayment.edoSignRefund' : 'app.deals.finalPayment.waitRefund';
    case 'WAIT_OVERPAYMENT':
      return 'app.deals.finalPayment.waitSellerReturn';
    case 'WAIT_RETURN_OVERPAYMENT_CONFIRMATION':
      return 'app.deals.finalPayment.waitBuyerConfirm';
    case 'WAIT_UPLOAD_UKD':
      return 'app.deals.finalPayment.waitUkd';
    default:
      return 'app.deals.finalPayment.processing';
  }
}

// Шаг «Окончательный расчёт» (id 6). По итоговому весу/качеству — доплата (факт > план) или возврат
// (факт < план), затем УКД. Содержимое расчёта (итог + документы + разбивка цены) — в DealFinalSettlement
// (общее с read-only ревизией завершённой сделки); здесь добавляем взаимное действие сторон (ЭДО/оплата).
export default function FinalPaymentStep({ dealId, info }: FinalPaymentStepProps) {
  const { t } = useTranslation();
  const ps = info.processingStep ?? '';
  const isBuyer = info.isBuyer === true;
  const isSeller = info.isSeller === true;

  const action = actionForState(ps, isBuyer, isSeller);
  const confirm = usePaymentConfirm(dealId, action?.kind ?? 'payUnderpayment');
  const [checked, setChecked] = useState(false);

  return (
    <Stack gap="lg">
      <DealFinalSettlement info={info} />

      <Divider />

      {ps === 'FINAL_PAYMENT_DONE' ? (
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size={32} radius="xl" variant="light" color="lime">
            <IconCircleCheck size={18} />
          </ThemeIcon>
          <Text fz="sm" fw={500}>
            {t('app.deals.finalPayment.done')}
          </Text>
        </Group>
      ) : action ? (
        <Stack gap="sm">
          <Checkbox
            checked={checked}
            onChange={(event) => setChecked(event.currentTarget.checked)}
            label={t(action.checkboxKey)}
            disabled={confirm.isPending}
          />
          <Button disabled={!checked || confirm.isPending} loading={confirm.isPending} onClick={() => confirm.mutate()} w="fit-content">
            {t(action.buttonKey)}
          </Button>
        </Stack>
      ) : (
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size={32} radius="xl" variant="light" color="brand">
            <IconClock size={18} />
          </ThemeIcon>
          <Text fz="sm">{t(waitKey(ps, isBuyer, isSeller))}</Text>
        </Group>
      )}
    </Stack>
  );
}
